import { FC, useMemo } from "react";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { FaClipboard, FaEdit, FaEnvelope, FaFileAlt, FaList, FaPlus, FaUserFriends } from "react-icons/fa";
import Dropdown from "react-bootstrap/esm/Dropdown";
import { Link } from "react-router-dom";
import { NavigationScreen } from "app/component/screen/NavigationScreen";
import { useReportVulnerabilities } from "app/api/graph/hook/use-report-vulnerabilities";
import { useRouteParams } from "app/route/route";
import {
    createReportVersionVulnerabilityRoute,
    editReportVersionRoute,
    newReportVersionRoute,
    reportRoute,
    reportUsersRoute,
    reportVersionAccessInformationRoute,
    reportVersionCustomEmailSendRoute,
    reportVersionDailyTestCommencementEmailSendRoute,
    reportVersionDailyTestCompletionEmailSendRoute,
    reportVersionDeliverableFilesRoute,
    reportVersionEngagementOverviewRoute,
    reportVersionRoute,
    reportVersionScopeRoute,
    reportVersionsRoute,
    reportVersionTestTypePrerequisitesRoute,
    reportVersionVulnerabilitiesRoute,
} from "app/route/Routes";
import { LoadingScreen } from "app/component/screen/LoadingScreen";
import { ErrorScreen } from "app/component/screen/ErrorScreen";
import { NumberOfVulnerabilitiesCard } from "app/component/report/component/NumberOfVulnerabilitiesCard";
import { VulnerabilityTimelineCard } from "app/component/report/component/VulnerabilityTimelineCard";
import { VulnerabilityListCard } from "app/component/report/component/VulnerabilityListCard";
import { ReportVersionsCard } from "app/component/report/component/ReportVersionsCard";
import { ReportVersionStatus, ReportVulnerabilityStatus, useReportOverviewQuery } from "app/api/graph/types";
import { reportGraphId } from "app/api/graph/helpers";
import { RouteBreadcrumbs } from "app/route/RouteBreadcrumbs";
import { Toolbar, ToolbarButtons, ToolbarTitle } from "app/component/util/Toolbar";
import { ButtonLink } from "app/component/button/ButtonLink";
import { LocationState } from "app/component/report-version/ReportVersionVulnerabilitiesScreen.tsx";
import { useReportVersionsList } from "app/api/graph/hook/use-report-versions-list.ts";
import { useReportPermissions } from "app/api/graph/hook/use-report-permissions.ts";
import { ReportVersionStatusHorizontalTimeline } from "app/component/report-version/component/ReportVersionStatusHorizontalTimeline";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";
import { useReportVersionPermissions } from "app/api/graph/hook/use-report-version-permissions";

export const ReportScreen: FC = () => {
    const { reportId } = useRouteParams(reportRoute);
    const {
        data,
        loading: loadingReport,
        error: reportError,
    } = useReportOverviewQuery({
        variables: { reportId: reportGraphId(reportId) },
    });
    const report = data?.report;
    const version = report?.latestVersion;
    const { canListUsers, canUpdate, canCreateReportVulnerability } = useReportPermissions(report?.permissions);
    const engagementOverviewPermissions = useGeneralPermissions(version?.engagementOverview?.permissions);
    const reportVersionPermissions = useReportVersionPermissions(version?.permissions);
    const scopePermissions = useGeneralPermissions(version?.scope?.permissions);
    const accessInformationPermissions = useGeneralPermissions(version?.accessInformation?.permissions);

    const {
        vulnerabilities,
        loading: loadingVulnerabilities,
        error: vulnerabilitiesError,
    } = useReportVulnerabilities(reportId);

    const openVulnerabilities = useMemo(
        () => vulnerabilities?.filter((v) => v.status === ReportVulnerabilityStatus.Open),
        [vulnerabilities],
    );
    const resolvedVulnerabilities = useMemo(
        () =>
            vulnerabilities?.filter(
                (v) =>
                    v.status === ReportVulnerabilityStatus.Resolved ||
                    v.status === ReportVulnerabilityStatus.VerifiedResolved,
            ),
        [vulnerabilities],
    );
    const riskAcceptedVulnerabilities = useMemo(
        () => vulnerabilities?.filter((v) => v.status === ReportVulnerabilityStatus.RiskAccepted),
        [vulnerabilities],
    );
    const informationalVulnerabilities = useMemo(
        () => vulnerabilities?.filter((v) => v.status === ReportVulnerabilityStatus.Informational),
        [vulnerabilities],
    );

    const { versions, loading: loadingVersions, error: versionsError } = useReportVersionsList(reportId);

    if (loadingReport || loadingVulnerabilities || loadingVersions) {
        return <LoadingScreen />;
    }
    if (reportError) {
        return <ErrorScreen title="Error" message={reportError.message} />;
    }
    if (vulnerabilitiesError) {
        return <ErrorScreen title="Error" message={vulnerabilitiesError.message} />;
    }
    if (versionsError) {
        return <ErrorScreen title="Error" message={versionsError.message} />;
    }
    if (!report || !version || !versions) {
        return <ErrorScreen title="Error" message="Not found." />;
    }

    return (
        <NavigationScreen>
            <Container fluid>
                <RouteBreadcrumbs route={reportRoute} />
                <Toolbar>
                    <ToolbarTitle>
                        <h1>{report.title}</h1>
                    </ToolbarTitle>
                    <ToolbarButtons>
                        {canListUsers ? (
                            <ButtonGroup className="me-2">
                                <ButtonLink to={reportUsersRoute.build({ reportId })}>
                                    <FaUserFriends /> Users
                                </ButtonLink>
                            </ButtonGroup>
                        ) : null}
                        <ButtonGroup className="me-2">
                            <ButtonLink
                                to={reportVersionRoute.build({ reportId, versionId: version._id })}
                                variant="info"
                            >
                                <FaFileAlt /> View report
                            </ButtonLink>
                            {canUpdate ? (
                                <>
                                    <ButtonLink to={newReportVersionRoute.build({ reportId })} variant="success">
                                        <FaPlus /> New version
                                    </ButtonLink>
                                    <ButtonLink
                                        to={editReportVersionRoute.build({ reportId, versionId: version._id })}
                                        variant="warning"
                                    >
                                        <FaEdit /> Edit version
                                    </ButtonLink>
                                </>
                            ) : null}
                        </ButtonGroup>

                        <ButtonGroup className="me-2">
                            <Dropdown>
                                <Dropdown.Toggle variant="info" id="dropdown-test-details">
                                    <FaClipboard /> Test details
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {engagementOverviewPermissions.canView && (
                                        <Dropdown.Item
                                            as={Link}
                                            to={reportVersionEngagementOverviewRoute.build({
                                                reportId,
                                                versionId: version._id,
                                            })}
                                        >
                                            Engagement overview
                                        </Dropdown.Item>
                                    )}
                                    {scopePermissions.canView && (
                                        <Dropdown.Item
                                            as={Link}
                                            to={reportVersionScopeRoute.build({
                                                reportId,
                                                versionId: version._id,
                                            })}
                                        >
                                            Scope
                                        </Dropdown.Item>
                                    )}
                                    {accessInformationPermissions.canView && (
                                        <Dropdown.Item
                                            as={Link}
                                            to={reportVersionAccessInformationRoute.build({
                                                reportId,
                                                versionId: version._id,
                                            })}
                                        >
                                            Access information
                                        </Dropdown.Item>
                                    )}
                                    <Dropdown.Item
                                        as={Link}
                                        to={reportVersionTestTypePrerequisitesRoute.build({
                                            reportId,
                                            versionId: version._id,
                                        })}
                                    >
                                        Prerequisites
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        as={Link}
                                        to={reportVersionDeliverableFilesRoute.build({
                                            reportId,
                                            versionId: version._id,
                                        })}
                                    >
                                        Deliverable files
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </ButtonGroup>

                        {(reportVersionPermissions.canSendCustomEmail ||
                            reportVersionPermissions.canSendDailyTestEmail) && (
                            <ButtonGroup>
                                <Dropdown>
                                    <Dropdown.Toggle variant="info" id="dropdown-send-email">
                                        <FaEnvelope /> Send email
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        {reportVersionPermissions.canSendCustomEmail && (
                                            <Dropdown.Item
                                                as={Link}
                                                to={reportVersionCustomEmailSendRoute.build({
                                                    reportId,
                                                    versionId: version._id,
                                                })}
                                            >
                                                Custom
                                            </Dropdown.Item>
                                        )}
                                        {reportVersionPermissions.canSendDailyTestEmail &&
                                            version.status === ReportVersionStatus.Testing && (
                                                <Dropdown.Item
                                                    as={Link}
                                                    to={reportVersionDailyTestCommencementEmailSendRoute.build({
                                                        reportId,
                                                        versionId: version._id,
                                                    })}
                                                >
                                                    Daily test commencement
                                                </Dropdown.Item>
                                            )}
                                        {reportVersionPermissions.canSendDailyTestEmail &&
                                            version.status === ReportVersionStatus.Testing && (
                                                <Dropdown.Item
                                                    as={Link}
                                                    to={reportVersionDailyTestCompletionEmailSendRoute.build({
                                                        reportId,
                                                        versionId: version._id,
                                                    })}
                                                >
                                                    Daily test completion
                                                </Dropdown.Item>
                                            )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </ButtonGroup>
                        )}
                    </ToolbarButtons>
                </Toolbar>

                <div className="my-3">
                    <ReportVersionStatusHorizontalTimeline currentStatus={version.status} />
                </div>

                <Row>
                    <Col lg={6}>
                        <NumberOfVulnerabilitiesCard
                            title={`Open vulnerabilities by severity (${openVulnerabilities?.length ?? 0})`}
                            vulnerabilities={openVulnerabilities ?? []}
                        />
                        <VulnerabilityTimelineCard vulnerabilities={vulnerabilities ?? []} />
                    </Col>
                    <Col lg={6}>
                        <VulnerabilityListCard
                            title="Open vulnerabilities"
                            vulnerabilities={openVulnerabilities ?? []}
                            buttons={
                                <ButtonGroup>
                                    <ButtonLink
                                        to={reportVersionVulnerabilitiesRoute.build({
                                            reportId: report._id,
                                            versionId: version._id,
                                        })}
                                        state={
                                            {
                                                initialFilters: { status: ReportVulnerabilityStatus.Open },
                                            } as LocationState
                                        }
                                        variant="info"
                                        size="sm"
                                    >
                                        <FaList />
                                    </ButtonLink>
                                    {canCreateReportVulnerability ? (
                                        <ButtonLink
                                            to={createReportVersionVulnerabilityRoute.build({
                                                reportId: report._id,
                                                versionId: version._id,
                                            })}
                                            variant="success"
                                            size="sm"
                                        >
                                            <FaPlus />
                                        </ButtonLink>
                                    ) : null}
                                </ButtonGroup>
                            }
                        />
                        <VulnerabilityListCard
                            title="Resolved vulnerabilities"
                            vulnerabilities={resolvedVulnerabilities ?? []}
                            buttons={
                                <ButtonGroup>
                                    <ButtonLink
                                        to={reportVersionVulnerabilitiesRoute.build({
                                            reportId: report._id,
                                            versionId: version._id,
                                        })}
                                        state={
                                            {
                                                initialFilters: { status: ReportVulnerabilityStatus.Resolved },
                                            } as LocationState
                                        }
                                        variant="info"
                                        size="sm"
                                    >
                                        <FaList />
                                    </ButtonLink>
                                </ButtonGroup>
                            }
                        />
                        <VulnerabilityListCard
                            title="Risk accepted vulnerabilities"
                            vulnerabilities={riskAcceptedVulnerabilities ?? []}
                            buttons={
                                <ButtonGroup>
                                    <ButtonLink
                                        to={reportVersionVulnerabilitiesRoute.build({
                                            reportId: report._id,
                                            versionId: version._id,
                                        })}
                                        state={
                                            {
                                                initialFilters: { status: ReportVulnerabilityStatus.RiskAccepted },
                                            } as LocationState
                                        }
                                        variant="info"
                                        size="sm"
                                    >
                                        <FaList />
                                    </ButtonLink>
                                </ButtonGroup>
                            }
                        />
                        <VulnerabilityListCard
                            title="Informational"
                            vulnerabilities={informationalVulnerabilities ?? []}
                            buttons={
                                <ButtonGroup>
                                    <ButtonLink
                                        to={reportVersionVulnerabilitiesRoute.build({
                                            reportId: report._id,
                                            versionId: version._id,
                                        })}
                                        state={
                                            {
                                                initialFilters: { status: ReportVulnerabilityStatus.Informational },
                                            } as LocationState
                                        }
                                        variant="info"
                                        size="sm"
                                    >
                                        <FaList />
                                    </ButtonLink>
                                </ButtonGroup>
                            }
                        />
                        <ReportVersionsCard
                            versions={versions}
                            buttons={
                                <ButtonGroup>
                                    <ButtonLink to={reportVersionsRoute.build({ reportId })} variant="info" size="sm">
                                        <FaList />
                                    </ButtonLink>
                                    {canUpdate ? (
                                        <ButtonLink
                                            to={newReportVersionRoute.build({ reportId })}
                                            variant="success"
                                            size="sm"
                                        >
                                            <FaPlus />
                                        </ButtonLink>
                                    ) : null}
                                </ButtonGroup>
                            }
                        />
                    </Col>
                </Row>
            </Container>
        </NavigationScreen>
    );
};
